import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import csvDownload from "json-to-csv-export";
import { DownloadOutlined } from "@ant-design/icons";
import { Tabs, ToolbarButton } from "../../../design-system";
import { categoryLabel } from "../../../common/components/CategoryPath";
import { formatNumber } from "../../../common/Number";
import { negotiationStatusToString } from "../../project/components/ProjectStatusCell";
import { Metrics } from "./components/Metrics";
import { SpendTreeTable, categoryName } from "./components/SpendTreeTable";
import { CATEGORY_PARAM, categoryCrumbs, VendorPage, vendorPageHref } from "../VendorPage";
import { useVendorSpend } from "./useVendorSpend";
import {
    categoryFromKey,
    categoryTree,
    findCategory,
    dealsIn,
    scopeTally,
    SpendTally,
    SpendView,
    vendorTree,
} from "./vendorSpend";
import classes from "./VendorsView.module.less";

// Whole units, as the savings progress bars show them ("$8M of $10M").
const dollars = (cents: number) => formatNumber(cents / 100, 0);

/** A safe file name: no path or reserved characters, single spaces. */
const csvFilename = (name: string) =>
    name
        .replace(/[\\/:*?"<>|]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();

/** Realized savings (green) against the target, as a banner number. */
const savingsLabel = (tally: SpendTally) => {
    if (tally.targetedSavings <= 0 && tally.realizedSavings <= 0) return "–";
    const realized = <span className={classes.realized}>{dollars(tally.realizedSavings)}</span>;
    return tally.targetedSavings > 0 ? (
        <>
            {realized} of {dollars(tally.targetedSavings)}
        </>
    ) : (
        realized
    );
};

/**
 * The vendor page: the spend tree, categories down to vendors and their
 * deals, rooted at the whole taxonomy or at one category. Categories come
 * from the org's own taxonomy when it has one, from the IL spend categories
 * otherwise.
 */
export const VendorsView = () => {
    const [searchParams] = useSearchParams();
    const categoryKey = searchParams.get(CATEGORY_PARAM);

    const { model } = useVendorSpend();
    const [view, setView] = useState<SpendView>("vendor");

    if (!model) return <VendorPage crumbs={[]} loading />;

    const tree = categoryTree(model);
    const current = categoryKey != null ? categoryFromKey(categoryKey) : undefined;
    // The tree shown: the whole taxonomy or the branches under one category,
    // or, per vendor, the vendors of that scope with their deals.
    const roots =
        view === "vendor"
            ? vendorTree(model, categoryKey)
            : current
              ? (findCategory(tree, current.key)?.children ?? [])
              : tree;
    const tally = scopeTally(model, categoryKey);

    // The title's crumbs: every ancestor of the current category as a link, then itself.
    const crumbs = current
        ? categoryCrumbs(current.path, categoryName(current), vendorPageHref)
        : [];

    // One row per deal of the scope: plain numbers (score 0 to 100, whole
    // dollars), no tallies.
    const handleDownload = () => {
        const wholeDollars = (cents: number) => Math.round(cents / 100);
        const data = dealsIn(model, categoryKey).map(({ deal, vendor, category }) => ({
            Category: category.path ? categoryLabel(category.path) : categoryName(category),
            Vendor: vendor.name,
            Deal: deal.name,
            Status: deal.negotiationStatus ? negotiationStatusToString(deal.negotiationStatus) : "",
            Score: deal.orderScore > 0 ? Math.round(deal.orderScore) : "",
            Value: Math.round(Number(deal.value) || 0),
            "Targeted savings": wholeDollars(Number(deal.targetedSavings) || 0),
            "Realized savings": wholeDollars(Number(deal.realizedSavings) || 0),
        }));
        const scope = current ? categoryName(current) : "All";
        csvDownload({
            data,
            headers: data.length ? Object.keys(data[0]) : [],
            filename: csvFilename(`${scope} deals ${new Date().toISOString().slice(0, 10)}`),
        });
    };

    return (
        <VendorPage crumbs={crumbs} subtitle="Your spend by vendor or by category.">
            <Metrics
                metrics={[
                    { label: "Vendors", value: tally.vendors },
                    { label: "Deals", value: tally.deals },
                    { label: "Analyzed spend", value: formatNumber(tally.value) },
                    { label: "Realized savings", value: savingsLabel(tally) },
                ]}
            />
            <div className={classes.table}>
                {/* The view switch on the left, the export on the right, in
                    their own row above the table. */}
                <div className={classes.toolbar}>
                    <Tabs
                        // No panes under the strip, so no gap for them.
                        tabBarStyle={{ marginBottom: 0 }}
                        items={[
                            { key: "vendor", label: "By vendor" },
                            { key: "category", label: "By category" },
                        ]}
                        activeKey={view}
                        onChange={(key) => setView(key as SpendView)}
                    />
                    <ToolbarButton icon={<DownloadOutlined />} onClick={handleDownload}>
                        Export
                    </ToolbarButton>
                </div>
                <SpendTreeTable
                    // A new table per view and root: its search, sort and
                    // expansion start over.
                    key={`${view}:${categoryKey ?? ""}`}
                    nodes={roots}
                    view={view}
                    categoryHref={(category) => vendorPageHref(category.key)}
                    emptyText={current ? "Nothing in this category" : "No vendors yet"}
                />
            </div>
        </VendorPage>
    );
};
